import React, { createContext, useContext, useEffect, useState } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return ctx;
};

const translations = {
  en: {
    common: {
      appName: 'Resume Builder',
      navTemplates: 'Templates',
      navExamples: 'Examples',
      navPricing: 'Pricing',
      navHelp: 'Help',
      login: 'Log In',
      signup: 'Sign Up',
      dashboard: 'Dashboard',
      editProfile: 'Edit Profile',
      logout: 'Logout',
      company: 'Company',
      candidate: 'Candidate',
      languageToggleLabel: 'Language',
      languageEnglish: 'English',
      languageSpanish: 'Español',
    },
    landing: {
      heroTitle: 'The Best Online Resume Builder',
      heroSubtitle:
        'Easily create a resume from any device with our best-in-class resume builder platform.',
      ctaCreateResume: 'Create Resume',
      ctaAIRecommendations: 'AI Recommendations',
      ctaAIAnalysis: 'AI Resume Analysis',
      statsMoreInterviews: 'More Interviews',
      statsHigherOffers: 'Higher Job Offers',
      statsResumeExamples: 'Resume Examples',
      statsProfessionalTemplates: 'Professional Templates',
      featuresTitle:
        'Get hired 36% faster with our feature-packed and easy-to-use resume builder',
      testimonialsTitle: 'What users say about Resume Builder',
      ctaBottomTitle: "Let's Land Your Dream Job Together",
      ctaBottomButton: 'Start Your Resume Now',
    },
    pricing: {
      title: 'Choose Your AI Plan',
      subtitle:
        'Unlock AI-powered resume recommendations and deep resume analysis with flexible credit packs or a monthly plan.',
      starterName: 'Starter AI Pack',
      starterDescription: 'Try AI with a small bundle of recommendations and analyses.',
      proName: 'Pro AI Pack',
      proDescription: 'Best value for active job seekers who want more AI help.',
      enterpriseName: 'Enterprise (Monthly)',
      enterpriseDescription: 'Unlimited AI for power users and serious job seekers.',
      oneTime: 'one-time',
      perMonth: 'per month',
      mostPopular: 'Most Popular',
      buyPack: 'Buy AI Pack',
      startMonthly: 'Start Monthly Plan',
      manageSubscription: 'Manage Subscription',
      footerTitle: "What's included in premium AI features?",
      footerP1:
        'Premium plans unlock all AI-powered tools, including AI Template Recommendations and AI Resume Analysis (for both builder resumes and uploaded files).',
      footerP2:
        'Free users can still build and download resumes using all basic templates, but AI features require an active Enterprise or Director subscription.',
      alreadyPremium: 'You already have an active premium subscription',
    },
    examples: {
      heroTitle: 'Resume Examples That Actually Work',
      heroSubtitle:
        'Use these curated examples as a starting point, then customize them with our AI tools to match your experience, target role, and industry.',
      cardWhatThisDoesWell: 'WHAT THIS EXAMPLE DOES WELL',
      useLayout: 'Use this layout in the builder',
      seeAIScore: 'See how AI would score this',
      bottomTitle: 'Ready to build your own standout resume?',
      bottomText:
        'Start from any example above, or jump straight into the builder and let AI guide you through every section.',
      openBuilder: 'Open the Resume Builder',
      getAIRecommendations: 'Get AI template recommendations',
    },
  },
  es: {
    common: {
      appName: 'Creador de CV',
      navTemplates: 'Plantillas',
      navExamples: 'Ejemplos',
      navPricing: 'Precios',
      navHelp: 'Ayuda',
      login: 'Iniciar sesión',
      signup: 'Crear cuenta',
      dashboard: 'Panel',
      editProfile: 'Editar perfil',
      logout: 'Cerrar sesión',
      company: 'Empresa',
      candidate: 'Candidato',
      languageToggleLabel: 'Idioma',
      languageEnglish: 'Inglés',
      languageSpanish: 'Español',
    },
    landing: {
      heroTitle: 'El mejor creador de CV en línea',
      heroSubtitle:
        'Crea tu CV fácilmente desde cualquier dispositivo con nuestra plataforma profesional.',
      ctaCreateResume: 'Crear CV',
      ctaAIRecommendations: 'Recomendaciones con IA',
      ctaAIAnalysis: 'Análisis de CV con IA',
      statsMoreInterviews: 'Más entrevistas',
      statsHigherOffers: 'Mejores ofertas laborales',
      statsResumeExamples: 'Ejemplos de CV',
      statsProfessionalTemplates: 'Plantillas profesionales',
      featuresTitle:
        'Consigue trabajo un 36% más rápido con nuestro creador de CV completo y fácil de usar',
      testimonialsTitle: 'Lo que dicen los usuarios sobre Creador de CV',
      ctaBottomTitle: 'Consigamos juntos tu trabajo soñado',
      ctaBottomButton: 'Empieza tu CV ahora',
    },
    pricing: {
      title: 'Elige tu plan de IA',
      subtitle:
        'Desbloquea recomendaciones de plantillas y análisis de CV con IA usando paquetes de créditos o un plan mensual.',
      starterName: 'Paquete IA Inicial',
      starterDescription: 'Prueba la IA con un pequeño paquete de recomendaciones y análisis.',
      proName: 'Paquete IA Pro',
      proDescription:
        'La mejor opción para candidatos activos que quieren más ayuda de la IA.',
      enterpriseName: 'Enterprise (Mensual)',
      enterpriseDescription:
        'IA ilimitada para usuarios intensivos y quienes buscan cambiar de carrera.',
      oneTime: 'pago único',
      perMonth: 'al mes',
      mostPopular: 'Más popular',
      buyPack: 'Comprar paquete IA',
      startMonthly: 'Empezar plan mensual',
      manageSubscription: 'Gestionar suscripción',
      footerTitle: '¿Qué incluyen las funciones premium de IA?',
      footerP1:
        'Los planes premium desbloquean todas las herramientas con IA, incluidas las recomendaciones de plantillas y el análisis de CV (para CV creados y archivos subidos).',
      footerP2:
        'Los usuarios gratuitos pueden crear y descargar CV con las plantillas básicas, pero las funciones de IA requieren una suscripción Enterprise o Director activa.',
      alreadyPremium: 'Ya tienes una suscripción premium activa',
    },
    examples: {
      heroTitle: 'Ejemplos de CV que realmente funcionan',
      heroSubtitle:
        'Usa estos ejemplos como punto de partida y después personalízalos con nuestra IA según tu experiencia, puesto objetivo e industria.',
      cardWhatThisDoesWell: 'LO QUE ESTE EJEMPLO HACE BIEN',
      useLayout: 'Usar este diseño en el editor',
      seeAIScore: 'Ver cómo la IA lo califica',
      bottomTitle: '¿Listo para crear tu propio CV destacado?',
      bottomText:
        'Empieza desde cualquier ejemplo o entra directamente al editor y deja que la IA te guíe sección por sección.',
      openBuilder: 'Abrir el editor de CV',
      getAIRecommendations: 'Obtener recomendaciones de plantillas con IA',
    },
  },
};

const getNested = (obj, path) =>
  path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    const saved = window.localStorage.getItem('resume_builder_lang');
    if (saved === 'en' || saved === 'es') {
      setLanguage(saved);
    }
  }, []);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    window.localStorage.setItem('resume_builder_lang', lang);
  };

  const toggleLanguage = () => {
    changeLanguage(language === 'en' ? 'es' : 'en');
  };

  const t = (key) => {
    const value = getNested(translations[language], key);
    if (value !== undefined) return value;
    // fallback to English if available
    const fallback = getNested(translations.en, key);
    return fallback !== undefined ? fallback : key;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

